import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import Confetti from 'react-confetti';
import { useAuth } from '../context/AuthContext';
import { chessAPI } from '../services/api';
import { chessDuelSocket, connectSocket, getSocket } from '../services/socket';
import { buildInviteLink } from '../utils/inviteLinks';
import {
  getGlobalSoundEnabled,
  setGlobalSoundEnabled,
  subscribeToGlobalSoundPreference,
} from '../utils/soundPreferences';
import QuickChat from '../components/QuickChat';
import '../styles/Flappy.css';

const INITIAL_CHESS_FEN = new Chess().fen();
const PIECE_VALUES = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
const PIECE_SYMBOLS = {
  w: { p: '♙', n: '♘', b: '♗', r: '♖', q: '♕' },
  b: { p: '♟', n: '♞', b: '♝', r: '♜', q: '♛' },
};
const STARTING_PIECE_COUNTS = {
  w: { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 },
  b: { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 },
};

function asNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatClock(ms) {
  const safe = Math.max(0, Math.floor(asNumber(ms)));
  const totalSec = Math.floor(safe / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function computeEffectiveClocks(match, serverNowMs, localTurnStartMs) {
  const whiteBase = asNumber(match?.white_time_left_ms);
  const blackBase = asNumber(match?.black_time_left_ms);

  if (!match || match.status !== 'in_progress' || !match.turn_started_at) {
    return { whiteMs: whiteBase, blackMs: blackBase };
  }

  let elapsed;
  if (localTurnStartMs != null) {
    // We observed this turn starting — use the local clock anchor to count elapsed.
    // We subtract the anchor from Date.now() to get a smooth local countdown.
    elapsed = Math.max(0, Date.now() - localTurnStartMs);
  } else {
    // Initial load / reconnect — fall back to server-offset approach since we
    // don't know how long this turn was underway before we connected.
    const turnStartedAtMs = new Date(match.turn_started_at).getTime();
    if (Number.isNaN(turnStartedAtMs)) return { whiteMs: whiteBase, blackMs: blackBase };
    elapsed = Math.max(0, serverNowMs - turnStartedAtMs);
  }

  if (match.turn === 'w') return { whiteMs: whiteBase - elapsed, blackMs: blackBase };
  return { whiteMs: whiteBase, blackMs: blackBase - elapsed };
}

function computeLegalTargets(game, fromSquare) {
  const moves = game.moves({ square: fromSquare, verbose: true });
  return Array.from(new Set(moves.map((m) => m.to)));
}

function findKingSquare(game, color) {
  try {
    const board = game.board();
    for (let r = 0; r < 8; r += 1) {
      for (let c = 0; c < 8; c += 1) {
        const piece = board[r][c];
        if (!piece) continue;
        if (piece.type === 'k' && piece.color === color) {
          const file = String.fromCharCode('a'.charCodeAt(0) + c);
          const rank = String(8 - r);
          return `${file}${rank}`;
        }
      }
    }
  } catch {
    // ignore
  }
  return null;
}

function isPromotionMove(game, fromSq, toSq) {
  try {
    const legal = game.moves({ square: fromSq, verbose: true });
    return legal.some((m) => m.to === toSq && m.promotion);
  } catch {
    return false;
  }
}

function buildCapturedSummary(fen) {
  try {
    const game = new Chess(fen || INITIAL_CHESS_FEN);
    const counts = {
      w: { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 },
      b: { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 },
    };

    for (const row of game.board()) {
      for (const piece of row) {
        if (!piece) continue;
        counts[piece.color][piece.type] += 1;
      }
    }

    const whiteCaptured = [];
    const blackCaptured = [];
    let whiteMaterial = 0;
    let blackMaterial = 0;

    ['q', 'r', 'b', 'n', 'p'].forEach((type) => {
      const blackMissing = Math.max(0, STARTING_PIECE_COUNTS.b[type] - counts.b[type]);
      const whiteMissing = Math.max(0, STARTING_PIECE_COUNTS.w[type] - counts.w[type]);

      for (let i = 0; i < blackMissing; i += 1) {
        whiteCaptured.push(PIECE_SYMBOLS.b[type]);
      }
      for (let i = 0; i < whiteMissing; i += 1) {
        blackCaptured.push(PIECE_SYMBOLS.w[type]);
      }

      whiteMaterial += blackMissing * PIECE_VALUES[type];
      blackMaterial += whiteMissing * PIECE_VALUES[type];
    });

    return {
      whiteCaptured,
      blackCaptured,
      materialDiff: whiteMaterial - blackMaterial,
    };
  } catch {
    return {
      whiteCaptured: [],
      blackCaptured: [],
      materialDiff: 0,
    };
  }
}

export default function ChessMatch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlTournamentId = searchParams.get('tournamentId');
  const { matchId } = useParams();
  const { user, token, refreshUser } = useAuth();

  const [match, setMatch] = useState(null);
  const activeTournamentId = match?.tournament_id || urlTournamentId;
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [fen, setFen] = useState(null);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalTargets, setLegalTargets] = useState([]);
  const [submittingMove, setSubmittingMove] = useState(false);
  const [pendingPromotion, setPendingPromotion] = useState(null); // { from, to }
  const [showWinnerConfetti, setShowWinnerConfetti] = useState(false);
  const [rematchCandidate, setrematchCandidate] = useState(null);
  const [rematchBusy, setrematchBusy] = useState(false);
  // Synchronous in-flight guard — prevents double-click from firing two API requests
  // before React re-renders the disabled button state.
  const rematchInFlightRef = useRef(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return getGlobalSoundEnabled();
  });
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reviewPly, setReviewPly] = useState(null);
  const [premove, setPremove] = useState(null); // { from, to, promotion? }
  const [isBerserk, setIsBerserk] = useState(false); // Local flag for current user

  const lastServerOffsetRef = useRef(0);
  const lastStatusRef = useRef(null);
  const optimisticFenRef = useRef(null);
  const optimisticSquaresRef = useRef(null);
  const inviteJoinAttemptedRef = useRef(false);
  const movesStripRef = useRef(null);
  const [boardSize, setBoardSize] = useState(320);
  const lastMoveCountRef = useRef(0);
  const chatListRef = useRef(null);
  const audioCtxRef = useRef(null);
  const showChatRef = useRef(false);
  const localSoundPlyRef = useRef(0);
  // Clock sync: tracks the last turn_started_at string we have seen so we can
  // detect when a new turn begins.  When we observe the change live (via socket)
  // we anchor to local Date.now() and count elapsed locally — avoiding jumps
  // caused by server_now_ms offset noise (socket vs HTTP have different latencies).
  const lastTurnStartedAtRef = useRef(undefined); // undefined = not yet initialised
  const localTurnStartTimeRef = useRef(null);     // local ms when we observed turn change

  const meId = asNumber(user?.id);
  const myColor = useMemo(() => {
    if (!match || !meId) return null;
    if (asNumber(match.white_id) === meId) return 'w';
    if (asNumber(match.black_id) === meId) return 'b';
    return null;
  }, [match, meId]);

  const orientation = myColor === 'b' ? 'black' : 'white';
  const isMyTurn = match?.status === 'in_progress' && myColor && String(match.turn) === myColor;

  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 100);
    return () => window.clearInterval(id);
  }, []);

  // Chess.com-style responsive board sizing:  
  // Compute board size from viewport minus fixed chrome (topbar + 2 player bars + bottom controls).  
  // Passed as boardWidth prop to Chessboard so pieces scale perfectly — no CSS override needed.  
  useEffect(() => {  
    const calc = () => {  
      const topbarH = 44;  
      const playerBarH = 52 * 2;  
      const bottomH = 96; // moves strip + action row  
      // Use a small margin on mobile so board notation labels stay on screen  
      const margin = window.innerWidth < 600 ? 12 : 4;  
      const availH = window.innerHeight - topbarH - playerBarH - bottomH - margin;  
      // On mobile use the full viewport width minus a small gutter for notation  
      const availW = window.innerWidth - margin;  
      const maxSize = window.innerWidth < 600 ? Math.min(availW, availH) : 720;  
      const size = Math.max(240, Math.floor(Math.min(availW, availH, maxSize)));  
      setBoardSize(size);  
    };  
    calc();  
    window.addEventListener('resize', calc);  
    window.addEventListener('orientationchange', calc);  
    return () => {  
      window.removeEventListener('resize', calc);  
      window.removeEventListener('orientationchange', calc);  
    };  
  }, []);

  useEffect(() => {
    setGlobalSoundEnabled(soundEnabled);
  }, [soundEnabled]);

  useEffect(() => subscribeToGlobalSoundPreference(setSoundEnabled), []);

  const ensureAudioContext = () => {
    if (typeof window === 'undefined') return null;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioCtx();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  };

  useEffect(() => {
    if (!soundEnabled || typeof window === 'undefined') return undefined;
    const unlockAudio = () => {
      ensureAudioContext();
    };
    window.addEventListener('pointerdown', unlockAudio, { passive: true });
    window.addEventListener('keydown', unlockAudio);
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, [soundEnabled]);

  useEffect(() => () => {
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  }, []);

  const effectiveServerNow = tick + (lastServerOffsetRef.current || 0);
  const clocks = useMemo(
    () => computeEffectiveClocks(match, effectiveServerNow, localTurnStartTimeRef.current),
    [match, effectiveServerNow]
  );

  const gameRef = useRef(null);

  const applyOptimisticFen = (f) => {
    if (!f) return;
    optimisticFenRef.current = f;
    try {
      gameRef.current = new Chess(f);
    } catch {
      // ignore
    }
    setFen(f);
  };

  const playMoveSoundNextFrame = (options = {}) => {
    if (!soundEnabled) return;
    // For local moves, we want instant feedback.
    // For socket updates, we slightly delay to align with animation if needed, 
    // but here we prioritize responsiveness.
    playMoveSound(options);
  };

  const syncGameFromMatch = (m) => {
    try {
      const f = String(m?.current_fen || '').trim();
      if (!f) return;
      const optimisticFen = optimisticFenRef.current;
      if (optimisticFen && f !== optimisticFen) return;
      const currentFen = typeof gameRef.current?.fen === 'function' ? gameRef.current.fen() : '';
      if (currentFen === f) {
        if (optimisticFen && f === optimisticFen) {
          optimisticFenRef.current = null;
          optimisticSquaresRef.current = null;
        }
        return;
      }
      gameRef.current = new Chess(f);
      setFen(f);
      if (optimisticFen && f === optimisticFen) {
        optimisticFenRef.current = null;
        optimisticSquaresRef.current = null;
      }
      setSelectedSquare(null);
      setLegalTargets([]);
      setPendingPromotion(null);
    } catch {
      // ignore
    }
  };

  const load = async () => {
    try {
      setError('');
      const mRes = await chessAPI.getMatch(matchId);
      const m = mRes.data;
      setMatch(m);
      setIsReconnecting(false);

      if (typeof m?.server_now_ms === 'number') {
        lastServerOffsetRef.current = m.server_now_ms - Date.now();
      }

      // On initial load / reconnect we record the turn reference but keep
      // localTurnStartTimeRef = null so the server-offset path is used.
      // We don’t know how much of this turn elapsed while we were disconnected.
      if (m?.turn_started_at !== lastTurnStartedAtRef.current) {
        lastTurnStartedAtRef.current = m?.turn_started_at || null;
        localTurnStartTimeRef.current = null;
      }

      syncGameFromMatch(m);

      const mvRes = await chessAPI.getMoves(matchId);
      setMoves(mvRes.data?.moves || []);

      if (lastStatusRef.current && lastStatusRef.current !== m.status && m.status === 'completed') {
        refreshUser?.();
      }
      lastStatusRef.current = m.status;
    } catch (e) {
      if (!e?.response) {
        setIsReconnecting(true);
        return;
      }
      setError(e.response?.data?.error || 'Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      const sock = getSocket();
      // Only poll if the socket is disconnected. Real-time updates handle sync perfectly when connected.
      if (!sock || !sock.connected) {
        void load();
      }
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    if (!matchId || !user?.id || !token) return;
    const socket = getSocket() || connectSocket(user.id, token);
    const joinMatchRoom = () => {
      console.log(`[ChessMatch] Attempting to join match room: ${matchId}`);
      setIsReconnecting(false);
      chessDuelSocket.join(matchId);
    };
    const handleDisconnect = (reason) => {
      console.warn(`[ChessMatch] Socket disconnected. Reason: ${reason}`);
      setIsReconnecting(true);
    };
    const handleConnectError = (error) => {
      console.error(`[ChessMatch] Socket connection error:`, error);
      setIsReconnecting(true);
    };
    const handleJoined = () => {
      console.log(`[ChessMatch] Socket 'joined' event received`);
      joinMatchRoom();
    };

    if (socket?.connected) {
      console.log(`[ChessMatch] Socket already connected. Joining room.`);
      joinMatchRoom();
    } else if (socket) {
      console.log(`[ChessMatch] Socket not connected yet. Waiting for 'connect' event.`);
      setIsReconnecting(true);
    }
    if (socket) {
      socket.on('connect', joinMatchRoom);
      socket.on('joined', handleJoined);
      socket.on('disconnect', handleDisconnect);
      socket.on('connect_error', handleConnectError);
    }

    const onUpdate = (payload = {}) => {
      const updated = payload?.match;
      console.log(`[ChessMatch] Received chess:match:update:`, payload);
      if (!updated) return;
      if (String(updated.id) !== String(matchId)) return;

      setMatch(updated);
      setIsReconnecting(false);
      if (typeof updated?.server_now_ms === 'number') {
        lastServerOffsetRef.current = updated.server_now_ms - Date.now();
      }

      // When turn_started_at changes while we’re connected, anchor the countdown
      // to our local clock.
      if (updated?.turn_started_at !== lastTurnStartedAtRef.current) {
        lastTurnStartedAtRef.current = updated?.turn_started_at || null;
        if (updated?.turn_started_at) {
          // Account for network transit & server processing time:
          // spent = (server_now when packet sent) - (turn_started_at)
          const turnStartedAtMs = new Date(updated.turn_started_at).getTime();
          const serverNowAtServer = typeof updated.server_now_ms === 'number'
            ? updated.server_now_ms
            : (Date.now() + lastServerOffsetRef.current);
            
          const spentOnServer = Math.max(0, serverNowAtServer - turnStartedAtMs);
          // Anchor the local stopwatch so it reflects the time already spent.
          localTurnStartTimeRef.current = Date.now() - spentOnServer;
        } else {
          localTurnStartTimeRef.current = null;
        }
      }

      // On rollback (illegal move rejected by server), clear optimistic state
      if (payload.rollback) {
        optimisticFenRef.current = null;
        optimisticSquaresRef.current = null;
        setPremove(null);
      }

      syncGameFromMatch(updated);

      // Use the embedded move from the payload — no HTTP call needed
      if (payload.move) {
        setMoves((prev) => {
          const exists = prev.some((m) => m.ply === payload.move.ply && m.match_id === payload.move.match_id);
          if (exists) return prev;
          return [...prev, payload.move];
        });
      }

      if (updated.status === 'completed') {
        refreshUser?.();
        // On game complete, fetch full moves list for the review panel
        chessAPI.getMoves(matchId).then((r) => setMoves(r.data?.moves || [])).catch(() => {});
      }
      lastStatusRef.current = updated.status;
    };

    chessDuelSocket.onMatchUpdate(onUpdate);

    const onRelay = (payload = {}) => {
      if (String(payload.matchId) !== String(matchId)) return;
      if (Number(payload.moverId) === meId) return; // ignore our own optimistic broadcast
      applyOptimisticFen(payload.fenAfter);
      playMoveSoundNextFrame({
        isCapture: payload.san?.includes('x'),
        isCheck: payload.san?.includes('+') || payload.san?.includes('#'),
      });
    };
    chessDuelSocket.onMoveRelay(onRelay);

    chessDuelSocket.onChatHistory((payload = {}) => {
      if (String(payload?.matchId) !== String(matchId)) return;
      setChatMessages(Array.isArray(payload?.messages) ? payload.messages : []);
      setUnreadChatCount(0);
    });
    chessDuelSocket.onChatMessage((payload = {}) => {
      if (String(payload?.matchId) !== String(matchId)) return;
      const msg = payload?.message;
      if (!msg?.id) return;
      setChatMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        if (!showChatRef.current && Number(msg.userId) !== meId) {
          setUnreadChatCount((cnt) => cnt + 1);
        }
        return [...prev, msg].slice(-40);
      });
    });

    chessDuelSocket.onRematchCreated(async (payload = {}) => {
      if (String(payload?.originalMatchId) !== String(matchId)) return;
      try {
        const res = await chessAPI.getMatch(payload.newMatchId);
        setrematchCandidate(res.data);
      } catch (e) {
        console.error('Failed to load rematch candidate via socket:', e);
      }
    });

    return () => {
      if (socket) {
        socket.off('connect', joinMatchRoom);
        socket.off('joined', handleJoined);
        socket.off('disconnect', handleDisconnect);
        socket.off('connect_error', handleConnectError);
      }
      chessDuelSocket.removeListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, meId, user?.id, token]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleOffline = () => setIsReconnecting(true);
    const handleOnline = () => {
      setIsReconnecting(false);
      void load();
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    if (showChat) {
      setUnreadChatCount(0);
    }
    showChatRef.current = showChat;
  }, [showChat]);

  useEffect(() => {
    refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusText = useMemo(() => {
    if (!match) return '';
    if (match.status === 'pending') return 'Pending';
    if (match.status === 'in_progress') return `In progress — ${match.turn === 'w' ? 'White' : 'Black'} to move`;
    if (match.status === 'completed') return 'Completed';
    if (match.status === 'cancelled') return 'Cancelled';
    return String(match.status);
  }, [match]);

  const winnerText = useMemo(() => {
    if (!match || match.status !== 'completed') return '';
    const winnerId = asNumber(match.winner_id);
    if (!winnerId) return 'Result: Draw';
    const name = winnerId === asNumber(match.white_id)
      ? (match.white_username || 'White')
      : (match.black_username || 'Black');
    return `Winner: ${name}`;
  }, [match]);

  const inactivityCancelled = useMemo(() => {
    if (!match) return false;
    if (match.status !== 'cancelled') return false;
    if (match.mode !== 'betting') return false;
    if (!match.started_at) return false;
    return moves.length === 0;
  }, [match, moves.length]);

  const whiteName = match?.white_username || match?.creator_username || 'White';
  const blackName = match?.black_username || match?.opponent_username || 'Black';
  const isSpectator = !!(match && match.status === 'in_progress' && !myColor);

  const topTimer = orientation === 'black'
    ? { label: 'White', name: whiteName, ms: clocks.whiteMs, isTurn: match?.turn === 'w' }
    : { label: 'Black', name: blackName, ms: clocks.blackMs, isTurn: match?.turn === 'b' };

  const bottomTimer = orientation === 'black'
    ? { label: 'Black', name: blackName, ms: clocks.blackMs, isTurn: match?.turn === 'b' }
    : { label: 'White', name: whiteName, ms: clocks.whiteMs, isTurn: match?.turn === 'w' };
  const topTimerName = isReconnecting ? `Reconnecting... ${topTimer.name}` : topTimer.name;
  const bottomTimerName = isReconnecting ? `Reconnecting... ${bottomTimer.name}` : bottomTimer.name;

  const movePairs = useMemo(() => {
    const pairs = [];
    for (let i = 0; i < moves.length; i += 2) {
      pairs.push({
        move: Math.floor(i / 2) + 1,
        white: moves[i] || null,
        black: moves[i + 1] || null,
      });
    }
    return pairs;
  }, [moves]);

  const livePly = moves.length;
  const effectiveReviewPly = reviewPly == null ? livePly : Math.max(0, Math.min(reviewPly, livePly));
  const isReviewing = effectiveReviewPly !== livePly;
  const displayFen = useMemo(() => {
    if (isReviewing) {
      if (effectiveReviewPly <= 0) return INITIAL_CHESS_FEN;
      return String(moves[effectiveReviewPly - 1]?.fen_after || INITIAL_CHESS_FEN);
    }
    return fen || INITIAL_CHESS_FEN;
  }, [effectiveReviewPly, isReviewing, moves, fen]);

  const boardFen = useMemo(() => {
    if (isReviewing) return displayFen;
    if (premove) {
      try {
        const g = new Chess(fen || INITIAL_CHESS_FEN);
        g.move({ from: premove.from, to: premove.to, promotion: premove.promotion || 'q' });
        return g.fen();
      } catch {
        return fen || INITIAL_CHESS_FEN;
      }
    }
    return fen || INITIAL_CHESS_FEN;
  }, [isReviewing, displayFen, premove, fen]);

  const reviewGame = useMemo(() => {
    try {
      return new Chess(displayFen || INITIAL_CHESS_FEN);
    } catch {
      return new Chess();
    }
  }, [displayFen]);
  const capturedSummary = useMemo(
    () => buildCapturedSummary(displayFen || fen || INITIAL_CHESS_FEN),
    [displayFen, fen]
  );
  const reviewMoveLabel = effectiveReviewPly > 0
    ? (moves[effectiveReviewPly - 1]?.san || `${moves[effectiveReviewPly - 1]?.from_sq || ''}${moves[effectiveReviewPly - 1]?.to_sq || ''}`)
    : 'Start';
  const canStepBackward = effectiveReviewPly > 0;
  const canStepForward = effectiveReviewPly < livePly;

  // Auto-scroll move strip to end when moves update
  useEffect(() => {
    const el = movesStripRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [moves.length]);

  useEffect(() => {
    const el = chatListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chatMessages]);

  // Natural Lichess-style woody click sound
  const playMoveSound = ({ isCapture = false, isCheck = false } = {}) => {
    if (!soundEnabled) return;
    try {
      const ctx = ensureAudioContext();
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});

      const now = ctx.currentTime;
      const sr = ctx.sampleRate;

      // --- Noise burst (the sharp transient "click") ---
      const noiseLen = Math.floor(sr * 0.028);
      const noiseBuf = ctx.createBuffer(1, noiseLen, sr);
      const ch = noiseBuf.getChannelData(0);
      for (let i = 0; i < noiseLen; i++) {
        ch[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / noiseLen, 4);
      }
      const noise = ctx.createBufferSource();
      const noiseGain = ctx.createGain();
      const noiseFilter = ctx.createBiquadFilter();
      noise.buffer = noiseBuf;
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.value = isCapture ? 1400 : 1000;
      noiseFilter.Q.value = 0.7;
      noiseGain.gain.setValueAtTime(isCapture ? 0.55 : 0.40, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.028);
      noise.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(ctx.destination);
      noise.start(now);

      // --- Low woody thump ---
      const thump = ctx.createOscillator();
      const thumpGain = ctx.createGain();
      thump.type = 'sine';
      thump.frequency.setValueAtTime(isCheck ? 320 : isCapture ? 220 : 180, now);
      thump.frequency.exponentialRampToValueAtTime(55, now + 0.06);
      thumpGain.gain.setValueAtTime(0.0001, now);
      thumpGain.gain.exponentialRampToValueAtTime(isCapture ? 0.38 : 0.28, now + 0.003);
      thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
      thump.connect(thumpGain);
      thumpGain.connect(ctx.destination);
      thump.start(now);
      thump.stop(now + 0.07);
    } catch { /* ignore */ }
  };

  const playNotificationSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = ensureAudioContext();
      if (!ctx) return;
      const now = ctx.currentTime;
      // Two quick gentle tones
      [523.25, 659.25].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, now + i * 0.12);
        g.gain.exponentialRampToValueAtTime(0.07, now + i * 0.12 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.18);
        osc.connect(g);
        g.connect(ctx.destination);
        osc.start(now + i * 0.12);
        osc.stop(now + i * 0.12 + 0.2);
      });
    } catch { /* ignore */ }
  };

  // --- PREMOVE LOGIC ---
  useEffect(() => {
    if (isMyTurn && premove && !submittingMove && !pendingPromotion) {
      const g = gameRef.current;
      if (!g) return;

      const { from, to, promotion } = premove;
      const clone = new Chess(g.fen());
      let moved;
      try {
        moved = clone.move({ from, to, promotion: promotion || 'q' });
      } catch {
        moved = null;
      }

      const pMove = premove; // capture for clearance
      setPremove(null); // Clear regardless of outcome to avoid loops

      if (moved) {
        setSubmittingMove(true);
        optimisticSquaresRef.current = { from, to };
        applyOptimisticFen(clone.fen());
        localSoundPlyRef.current = Math.max(localSoundPlyRef.current, moves.length + 1);
        playMoveSound({ isCapture: !!moved.captured, isCheck: clone.isCheck() });

        submitMoveToServer({ from, to, promotion: promotion || 'q' })
          .catch((e) => {
            setError(e.response?.data?.error || 'Premove failed');
            optimisticFenRef.current = null;
            optimisticSquaresRef.current = null;
            syncGameFromMatch(match);
          })
          .finally(() => {
            setSubmittingMove(false);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyTurn, fen, premove]);

  // Handle Opponent Joined Sound
  const lastOpponentIdRef = useRef(null);
  useEffect(() => {
    // Play sound when opponent joins a pending match or connects to an ongoing one
    if (match?.opponent_id && !lastOpponentIdRef.current && asNumber(match.opponent_id) !== meId) {
      if (typeof playNotificationSound === 'function') {
        playNotificationSound();
      }
    }
    lastOpponentIdRef.current = match?.opponent_id;
  }, [match?.opponent_id, meId]);

  const handleCancelPmove = () => {
    setPremove(null);
  };

  useEffect(() => {
    if (!soundEnabled) {
      lastMoveCountRef.current = moves.length;
      return;
    }
    if (moves.length <= 0) {
      lastMoveCountRef.current = 0;
      return;
    }
    if (moves.length <= lastMoveCountRef.current) return;
    const isLocallyPredicted = localSoundPlyRef.current > 0 && moves.length <= localSoundPlyRef.current;
    lastMoveCountRef.current = moves.length;
    if (isLocallyPredicted) {
      if (moves.length >= localSoundPlyRef.current) {
        localSoundPlyRef.current = 0;
      }
      return;
    }

    const lastMove = moves[moves.length - 1];
    const san = String(lastMove?.san || '');
    playMoveSoundNextFrame({
      isCapture: san.includes('x'),
      isCheck: san.includes('+') || san.includes('#'),
    });
  }, [moves.length, soundEnabled]);

  const isParticipant = !!(match && (
    asNumber(match.creator_id) === meId ||
    asNumber(match.opponent_id) === meId ||
    asNumber(match.white_id) === meId ||
    asNumber(match.black_id) === meId
  ));
  const isCompleted = match?.status === 'completed';
  const winnerId = asNumber(match?.winner_id);
  const didIWin = Boolean(isCompleted && isParticipant && winnerId && winnerId === meId);
  const didILose = Boolean(isCompleted && isParticipant && winnerId && winnerId !== meId);

  const RematchOpponentId = useMemo(() => {
    if (!match || !isParticipant) return null;
    const creatorId = asNumber(match.creator_id);
    const opponentId = asNumber(match.opponent_id);
    if (!creatorId || !opponentId) return null;
    if (creatorId === meId) return opponentId;
    if (opponentId === meId) return creatorId;
    return null;
  }, [match, isParticipant, meId]);

  const RematchMode = String(match?.mode || (asNumber(match?.wager) > 0 ? 'betting' : 'friendly'));
  const RematchWager = asNumber(match?.wager);
  const RematchTimeControlSeconds = asNumber(match?.time_control_seconds) || 300;

  const canAcceptRematch = Boolean(
    rematchCandidate
    && asNumber(rematchCandidate.opponent_id) === meId
    && !rematchCandidate.accepted_at
  );

  const canCreateRematch = Boolean(isCompleted && isParticipant && !rematchCandidate);

  const canAccept = !!(
    match &&
    match.status === 'pending' &&
    asNumber(match.opponent_id) === meId &&
    !match.accepted_at
  );



  const canCancel = !!(match && match.status === 'pending' && asNumber(match.creator_id) === meId && !match.opponent_id);
  const canAbort = !!(match && (match.status === 'in_progress' || match.status === 'pending') && isParticipant && moves.length === 0);
  const canResign = !!(match && match.status === 'in_progress' && myColor && moves.length > 0);

  const isDrawOfferedByMe = !!(match?.metadata?.draw_offered_by && asNumber(match.metadata.draw_offered_by) === meId);
  const isDrawOfferedByOpponent = !!(match?.metadata?.draw_offered_by && asNumber(match.metadata.draw_offered_by) !== meId);
  const canOfferDraw = !!(match && match.status === 'in_progress' && isParticipant && !isDrawOfferedByMe && !isDrawOfferedByOpponent);
  const canAcceptDraw = !!(match && match.status === 'in_progress' && isParticipant && isDrawOfferedByOpponent);
  const canJoinOpenRoom = !!(
    match
    && match.status === 'pending'
    && !match.opponent_id
    && !isParticipant
  );
  const isRoomFullForViewer = !!(
    match
    && match.status === 'pending'
    && match.opponent_id
    && !isParticipant
    && !canAccept
  );

  const canBerserk = !!(
    match
    && match.tournament_id
    && (match.status === 'pending' || (match.status === 'in_progress' && moves.length < 2))
    && isParticipant
    && !isBerserk
    && !match.metadata?.berserk_white && !match.metadata?.berserk_black
  );
  const inviteLink = match?.id ? buildInviteLink(`/chess/duel/${match.id}`) : '';

  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        window.prompt('Copy Chess invite link:', inviteLink);
      }
    } catch {
      window.prompt('Copy Chess invite link:', inviteLink);
    }
  };

  const sendChatMessage = async () => {
    const text = String(chatInput || '').trim();
    if (!text) return;

    await new Promise((resolve) => {
      chessDuelSocket.sendChat({ matchId, text }, (resp) => {
        if (!resp?.ok) {
          setError(resp?.error || 'Failed to send message');
        } else {
          setChatInput('');
        }
        resolve();
      });
    });
  };

  useEffect(() => {
    if (!didIWin) {
      setShowWinnerConfetti(false);
      return undefined;
    }

    setShowWinnerConfetti(true);
    const timer = window.setTimeout(() => setShowWinnerConfetti(false), 4500);
    return () => window.clearTimeout(timer);
  }, [didIWin, match?.id]);

  // Auto-return to tournament after completion
  useEffect(() => {
    if ((match?.status === 'completed' || isCompleted) && activeTournamentId) {
      const timer = window.setTimeout(() => {
        navigate(`/chess/tournaments/${activeTournamentId}`);
      }, 4500);
      return () => window.clearTimeout(timer);
    }
  }, [match?.status, isCompleted, activeTournamentId, navigate]);

  useEffect(() => {
    const tryAutoJoinFromInvite = async () => {
      if (!canJoinOpenRoom) return;
      if (inviteJoinAttemptedRef.current) return;
      inviteJoinAttemptedRef.current = true;

      try {
        setError('');
        await chessAPI.joinMatch(matchId);
        refreshUser?.();
        await load();
      } catch (e) {
        const msg = String(e?.response?.data?.error || '').toLowerCase();
        if (msg.includes('full') || msg.includes('joined') || e?.response?.status === 409) {
          setError('This room is full. You can watch the game live.');
        } else {
          setError(e.response?.data?.error || 'Unable to join this room');
        }
      }
    };

    void tryAutoJoinFromInvite();
  }, [canJoinOpenRoom, matchId, refreshUser]);

  useEffect(() => {
    let alive = true;

    const loadrematchCandidate = async () => {
      if (!isCompleted || !isParticipant || !RematchOpponentId) {
        if (alive) setrematchCandidate(null);
        return;
      }

      try {
        const res = await chessAPI.listChallenges();
        const items = Array.isArray(res?.data) ? res.data : [];

        const matchPair = [asNumber(match?.creator_id), asNumber(match?.opponent_id)].sort((a, b) => a - b).join(':');
        const found = items.find((item) => {
          if (!item || String(item.status) !== 'pending') return false;
          if (String(item.id) === String(match?.id)) return false;

          const itemPair = [asNumber(item.creator_id), asNumber(item.opponent_id)].sort((a, b) => a - b).join(':');
          if (itemPair !== matchPair) return false;

          if (String(item.mode || '') !== RematchMode) return false;
          if (asNumber(item.wager) !== RematchWager) return false;
          if (asNumber(item.time_control_seconds) !== RematchTimeControlSeconds) return false;

          return true;
        }) || null;

        if (alive) setrematchCandidate(found);
      } catch {
        if (alive) setrematchCandidate(null);
      }
    };

    loadrematchCandidate();
    const intervalId = window.setInterval(loadrematchCandidate, 3000);
    return () => {
      alive = false;
      window.clearInterval(intervalId);
    };
  }, [isCompleted, isParticipant, RematchOpponentId, match?.id, match?.creator_id, match?.opponent_id, RematchMode, RematchWager, RematchTimeControlSeconds]);

  const handleRematchCreate = async () => {
    if (!RematchOpponentId) {
      setError('Rematch is only available after both players are assigned.');
      return;
    }
    // Synchronous guard: block concurrent calls before React re-renders
    if (rematchInFlightRef.current) return;
    rematchInFlightRef.current = true;

    try {
      setrematchBusy(true);
      setError('');
      const res = await chessAPI.rematch(matchId);
      refreshUser?.();
      navigate(`/chess/duel/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create Rematch challenge');
      // Reset ref on error so the user can try again
      rematchInFlightRef.current = false;
    } finally {
      setrematchBusy(false);
    }
  };

  const handleRematchAccept = async () => {
    if (!rematchCandidate) {
      setError('No Rematch challenge available yet.');
      return;
    }

    try {
      setrematchBusy(true);
      setError('');

      if (asNumber(rematchCandidate.opponent_id) === meId && !rematchCandidate.accepted_at) {
        await chessAPI.acceptMatch(rematchCandidate.id);
      } else if (!rematchCandidate.opponent_id) {
        await chessAPI.joinMatch(rematchCandidate.id);
      }

      refreshUser?.();
      navigate(`/chess/duel/${rematchCandidate.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to accept Rematch challenge');
    } finally {
      setrematchBusy(false);
    }
  };

  const handleRematchDecline = async () => {
    if (!rematchCandidate) {
      setError('No Rematch challenge available yet.');
      return;
    }

    try {
      setrematchBusy(true);
      setError('');
      await chessAPI.cancelMatch(rematchCandidate.id);
      setrematchCandidate(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to decline Rematch challenge');
    } finally {
      setrematchBusy(false);
    }
  };

  const handleBerserk = async () => {
    try {
      setError('');
      // In a real implementation, this would call a POST /chess/matches/:id/berserk
      setIsBerserk(true);
      // The backend should also update the match via socket.
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to go Berserk');
    }
  };

  const lastMoveSquares = useMemo(() => {
    if (!isReviewing && optimisticSquaresRef.current) return optimisticSquaresRef.current;
    if (!moves || effectiveReviewPly === 0) return null;
    const last = moves[effectiveReviewPly - 1];
    const from = String(last?.from_sq || '').trim();
    const to = String(last?.to_sq || '').trim();
    if (!/^[a-h][1-8]$/.test(from) || !/^[a-h][1-8]$/.test(to)) return null;
    return { from, to };
  }, [moves, effectiveReviewPly, isReviewing, fen]);

  const checkSquare = useMemo(() => {
    try {
      if (!reviewGame?.isCheck()) return null;
      return findKingSquare(reviewGame, reviewGame.turn());
    } catch {
      return null;
    }
  }, [reviewGame]);

  const premoveStyles = useMemo(() => {
    const styles = {};
    if (premove) {
      // Distinct reddish/purple highlight for premoves
      styles[premove.from] = { backgroundColor: 'rgba(239, 68, 68, 0.4)' };
      styles[premove.to] = { backgroundColor: 'rgba(239, 68, 68, 0.4)' };
    }
    return styles;
  }, [premove]);

  const submitMoveToServer = ({ from, to, promotion = 'q' }) => {
    return new Promise((resolve, reject) => {
      chessDuelSocket.submitMove(matchId, { from, to, promotion }, (resp) => {
        if (resp?.ok) {
          // Apply the authoritative match + move from the ack immediately
          // The chess:match:update event will also fire and is a no-op since FEN matches
          if (resp.match) {
            setMatch(resp.match);
            if (typeof resp.match.server_now_ms === 'number') {
              lastServerOffsetRef.current = resp.match.server_now_ms - Date.now();
            }

            // Also anchor the mover's local clock immediately for smoothness
            if (resp.match.turn_started_at !== lastTurnStartedAtRef.current) {
              lastTurnStartedAtRef.current = resp.match.turn_started_at || null;
              if (resp.match.turn_started_at) {
                const turnStartedAtMs = new Date(resp.match.turn_started_at).getTime();
                const serverNowAtServer = typeof resp.match.server_now_ms === 'number'
                  ? resp.match.server_now_ms
                  : (Date.now() + lastServerOffsetRef.current);
                const spentOnServer = Math.max(0, serverNowAtServer - turnStartedAtMs);
                localTurnStartTimeRef.current = Date.now() - spentOnServer;
              } else {
                localTurnStartTimeRef.current = null;
              }
            }

            syncGameFromMatch(resp.match);
          }
          if (resp.move) {
            setMoves((prev) => {
              const exists = prev.some((m) => m.ply === resp.move.ply && m.match_id === resp.move.match_id);
              if (exists) return prev;
              return [...prev, resp.move];
            });
          }
          resolve(resp);
        } else {
          reject(new Error(resp?.error || 'Move failed'));
        }
      });
    });
  };

  const onSquareClick = async (square) => {
    try {
      setError('');
      if (!match || match.status !== 'in_progress') return;
      if (!myColor) return;
      if (!gameRef.current) return;
      if (submittingMove) return;
      if (pendingPromotion) return;
      if (isReviewing) return;

      const g = gameRef.current;

      if (!selectedSquare) {
        const piece = g.get(square);

        if (!isMyTurn) {
          if (premove) {
            // Cancel existing premove when clicking anywhere or re-selecting a piece
            setPremove(null);
            setSelectedSquare(null);
            setLegalTargets([]);
            return;
          }
          // PREMOVE: select own piece as premove source
          if (piece && piece.color === myColor) {
            setSelectedSquare(square);
            setLegalTargets(computeLegalTargets(g, square)); // shows where it *could* go (dimmed hint)
          }
          return;
        }

        // Normal turn: select own piece
        if (!piece) return;
        if (piece.color !== myColor) return;
        setSelectedSquare(square);
        setLegalTargets(computeLegalTargets(g, square));
        return;
      }

      if (square === selectedSquare) {
        setSelectedSquare(null);
        setLegalTargets([]);
        return;
      }

      if (!isMyTurn) {
        if (premove) {
          // Cancel existing premove on click
          setPremove(null);
          setSelectedSquare(null);
          setLegalTargets([]);
          return;
        }
        // PREMOVE: pick target square
        const piece = g.get(square);
        if (piece && piece.color === myColor) {
          // Re-select different own piece as premove source
          setSelectedSquare(square);
          setLegalTargets(computeLegalTargets(g, square));
          return;
        }
        // Commit the premove
        setPremove({ from: selectedSquare, to: square });
        setSelectedSquare(null);
        setLegalTargets([]);
        return;
      }

      // Pre-validate with a cloned game.
      const clone = new Chess(g.fen());
      let moved;
      try {
        moved = clone.move({ from: selectedSquare, to: square, promotion: 'q' });
      } catch {
        moved = null;
      }

      if (!moved) {
        // Allow re-select another own piece.
        const piece = g.get(square);
        if (piece && piece.color === myColor) {
          setSelectedSquare(square);
          setLegalTargets(computeLegalTargets(g, square));
          return;
        }
        setError('Illegal move');
        return;
      }

      const optimisticFen = clone.fen();
      const isCapture = Boolean(moved?.captured);
      const isCheck = clone.isCheck();

      // Promotion picker — do NOT apply any optimistic FEN yet.
      // We don't know which piece the user will choose, so setting a queen-based
      // FEN here would poison optimisticFenRef and block the authoritative update.
      if (isPromotionMove(g, selectedSquare, square)) {
        setSelectedSquare(null);
        setLegalTargets([]);
        setPendingPromotion({ from: selectedSquare, to: square });
        return;
      }

      setSubmittingMove(true);
      optimisticSquaresRef.current = { from: selectedSquare, to: square };
      applyOptimisticFen(optimisticFen);
      localSoundPlyRef.current = Math.max(localSoundPlyRef.current, moves.length + 1);
      playMoveSoundNextFrame({ isCapture, isCheck });
      setSelectedSquare(null);
      setLegalTargets([]);

      try {
        await submitMoveToServer({ from: selectedSquare, to: square, promotion: 'q' });
      } catch (e) {
        setError(e.response?.data?.error || 'Failed to submit move');
        optimisticFenRef.current = null;
        optimisticSquaresRef.current = null;
        syncGameFromMatch(match);
      } finally {
        setSubmittingMove(false);
      }
    } catch (e) {
      setError(e?.message || 'Action failed');
      setSubmittingMove(false);
    }
  };

  const onDrop = async (sourceSquare, targetSquare) => {
    if (!match || (match.status !== 'in_progress' && match.status !== 'pending')) return false;
    if (!myColor) return false;
    if (!gameRef.current) return false;
    if (submittingMove) return false;
    if (pendingPromotion) return false;
    if (isReviewing) return false;

    const isWhiteStart = match.status === 'pending' && myColor === 'w';
    const isActuallyMyTurn = isMyTurn || isWhiteStart;

    if (!isActuallyMyTurn) {
      // PREMOVE ATTEMPT
      setPremove({ from: sourceSquare, to: targetSquare });
      return true;
    }

    const g = gameRef.current;
    const piece = g.get(sourceSquare);
    if (!piece || piece.color !== myColor) return false;

    const clone = new Chess(g.fen());
    let moved;
    try {
      moved = clone.move({ from: sourceSquare, to: targetSquare, promotion: 'q' });
    } catch {
      moved = null;
    }
    if (!moved) {
      setError('Illegal move');
      return false;
    }

    const optimisticFen = clone.fen();
    const isCapture = Boolean(moved?.captured);
    const isCheck = clone.isCheck();

    // Promotion picker — do NOT apply any optimistic FEN yet.
    if (isPromotionMove(g, sourceSquare, targetSquare)) {
      setSelectedSquare(null);
      setLegalTargets([]);
      setPendingPromotion({ from: sourceSquare, to: targetSquare });
      return true;
    }

    setSubmittingMove(true);
    optimisticSquaresRef.current = { from: sourceSquare, to: targetSquare };
    applyOptimisticFen(optimisticFen);
    localSoundPlyRef.current = Math.max(localSoundPlyRef.current, moves.length + 1);
    playMoveSoundNextFrame({ isCapture, isCheck });
    setSelectedSquare(null);
    setLegalTargets([]);

    try {
      await submitMoveToServer({ from: sourceSquare, to: targetSquare, promotion: 'q' });
      return true;
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit move');
      optimisticFenRef.current = null;
      optimisticSquaresRef.current = null;
      syncGameFromMatch(match);
      return false;
    } finally {
      setSubmittingMove(false);
    }
  };

  const customSquareStyles = useMemo(() => {
    const styles = {};
    if (selectedSquare) {
      // Premove selection: red/orange tint (Lichess-style). Normal: yellow tint.
      const isPremoveSelect = !isMyTurn;
      styles[selectedSquare] = isPremoveSelect
        ? { background: 'radial-gradient(circle, rgba(239,68,68,0.45) 0%, rgba(239,68,68,0.25) 70%, rgba(239,68,68,0) 72%)' }
        : { background: 'radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(250,204,21,0.18) 70%, rgba(250,204,21,0) 72%)' };
    }
    for (const sq of legalTargets) {
      const isPremoveSelect = !isMyTurn;
      styles[sq] = isPremoveSelect
        ? { background: 'radial-gradient(circle, rgba(239,68,68,0.22) 0%, rgba(239,68,68,0.10) 70%, rgba(239,68,68,0) 72%)' }
        : { background: 'radial-gradient(circle, rgba(16,185,129,0.28) 0%, rgba(16,185,129,0.14) 70%, rgba(16,185,129,0) 72%)' };
    }

    if (lastMoveSquares) {
      styles[lastMoveSquares.from] = {
        ...(styles[lastMoveSquares.from] || {}),
        boxShadow: 'inset 0 0 0 3px rgba(59,130,246,0.35)',
      };
      styles[lastMoveSquares.to] = {
        ...(styles[lastMoveSquares.to] || {}),
        boxShadow: 'inset 0 0 0 3px rgba(59,130,246,0.55)',
      };
    }

    if (checkSquare) {
      styles[checkSquare] = {
        ...(styles[checkSquare] || {}),
        boxShadow: 'inset 0 0 0 3px rgba(239,68,68,0.7)',
      };
    }

    return styles;
  }, [selectedSquare, legalTargets, lastMoveSquares, checkSquare, isMyTurn]);

  const getCapturedDisplay = (label) => {
    if (label === 'White') {
      return {
        pieces: capturedSummary.whiteCaptured,
        diff: capturedSummary.materialDiff > 0 ? `+${capturedSummary.materialDiff}` : '',
      };
    }
    return {
      pieces: capturedSummary.blackCaptured,
      diff: capturedSummary.materialDiff < 0 ? `+${Math.abs(capturedSummary.materialDiff)}` : '',
    };
  };

  if (!match && loading) return <div className="cm-loading">Loading match...</div>;
  if (!match) return <div className="cm-toast cm-toast--error">{error || 'Match not found'}</div>;

  return (
    <div className="chess-match-page chess-match-page--chess" style={{ '--board-size': `${boardSize}px` }}>
      {showWinnerConfetti && <Confetti recycle={false} numberOfPieces={260} />}

      {showChat && (
        <div
          onClick={() => setShowChat(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 12,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(360px, calc(100vw - 24px))',
              maxHeight: 'min(70vh, 520px)',
              borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(19,23,34,0.96)',
              overflow: 'hidden',
              boxShadow: '0 18px 60px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Match Chat</div>
              <button className="cm-icon-btn" onClick={() => setShowChat(false)} title="Close chat">✖</button>
            </div>
            <div ref={chatListRef} style={{ maxHeight: 'calc(70vh - 116px)', overflowY: 'auto', padding: '10px 12px', display: 'grid', gap: 8 }}>
              {chatMessages.length === 0 && (
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>No messages yet.</div>
              )}
              {chatMessages.map((message) => (
                <div key={message.id} style={{ fontSize: 12, lineHeight: 1.4 }}>
                  <div style={{ color: Number(message.userId) === meId ? '#facc15' : 'rgba(255,255,255,0.75)', fontWeight: 700 }}>
                    {message.username}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.88)' }}>{message.text}</div>
                </div>
              ))}
            </div>
            {isParticipant && (
              <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void sendChatMessage();
                    }
                  }}
                  maxLength={240}
                  placeholder="Type a message"
                  style={{ flex: 1, borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.2)', color: '#fff', padding: '10px 12px', outline: 'none' }}
                />
                <button className="cm-btn cm-btn--primary" onClick={() => void sendChatMessage()}>
                  Send
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── TOP BAR: nav + match info ─── */}
      <header className="cm-topbar">
        <button
          className="cm-icon-btn"
          onClick={() => activeTournamentId ? navigate(`/chess/tournaments/${activeTournamentId}`) : navigate('/chess')}
          title={activeTournamentId ? "Back to Tournament" : "Back to lobby"}
        >
          <span>←</span>
        </button>
        <div className="cm-topbar-center">
          <span className="cm-topbar-title">Chess #{matchId}</span>
          {match?.mode && <span className="cm-badge">{match.mode}</span>}
          {asNumber(match?.wager) > 0 && <span className="cm-badge cm-badge--gold">{match.wager} wkp</span>}
          {isReviewing && <span className="cm-badge cm-badge--review">Review</span>}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="cm-icon-btn" onClick={() => setSoundEnabled((prev) => !prev)} title="Toggle sound">
            <span>{soundEnabled ? '🔊' : '🔇'}</span>
          </button>
          <button className="cm-icon-btn" onClick={() => navigate('/dashboard')} title="Dashboard">
            <span>🏠</span>
          </button>
        </div>
      </header>

      {error && !isReconnecting && <div className="cm-toast cm-toast--error">{error}</div>}

      {/* ─── GAME STACK: opponent bar + board + my bar, all same width, zero gap ─── */}
      <div className="cm-game-stack">

      {/* ─── OPPONENT TIMER (top) ─── */}
      <div className={`cm-player-bar${topTimer.isTurn ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__avatar">{topTimer.label === 'Black' ? '♚' : '♔'}</span>
          <div className="cm-player-bar__meta">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="cm-player-bar__name">{topTimerName}</span>
              <span className="cm-player-bar__rating">
              ⭐ {topTimer.label === 'Black' ? match?.black_rating || 1200 : match?.white_rating || 1200}
            </span>
              {(topTimer.label === 'Black' ? asNumber(match?.black_streak) : asNumber(match?.white_streak)) >= 3 && (
                <span style={{ fontSize: 11 }}>🔥 {topTimer.label === 'Black' ? match?.black_streak : match?.white_streak}</span>
              )}
            </div>
            <div className="cm-captured-row">
              {getCapturedDisplay(topTimer.label).pieces.map((piece, index) => (
                <span key={`${topTimer.label}-cap-${index}`} className="cm-captured-piece">{piece}</span>
              ))}
              {getCapturedDisplay(topTimer.label).diff && (
                <span className="cm-captured-diff">{getCapturedDisplay(topTimer.label).diff}</span>
              )}
            </div>
          </div>
        </div>
        <div className={`cm-clock${topTimer.isTurn ? ' cm-clock--ticking' : ''}`}>{formatClock(topTimer.ms)}</div>
      </div>

      {/* ─── BOARD ─── */}
      <div className="cm-board-wrap" onContextMenu={(e) => { e.preventDefault(); setPremove(null); }}>
        {loading && <div className="cm-loading">Loading…</div>}
        <Chessboard
          boardWidth={boardSize}
          position={boardFen}
          onSquareClick={onSquareClick}
          onPieceDrop={onDrop}
          boardOrientation={orientation}
          arePiecesDraggable={true}
          areArrowsAllowed
          showBoardNotation
          customSquareStyles={{
            ...customSquareStyles,
            ...premoveStyles
          }}
          customDarkSquareStyle={{ backgroundColor: '#b58863' }}
          customLightSquareStyle={{ backgroundColor: '#f0d9b5' }}
          animationDuration={isReviewing ? 140 : 220}
        />

        {/* Promotion overlay */}
        {pendingPromotion && (
          <div className="cm-promo-overlay">
            <div className="cm-promo-dialog">
              <span className="cm-promo-title">Promote to:</span>
              <div className="cm-promo-options">
                {[
                  { p: 'q', label: '♛', title: 'Queen' },
                  { p: 'r', label: '♜', title: 'Rook' },
                  { p: 'b', label: '♝', title: 'Bishop' },
                  { p: 'n', label: '♞', title: 'Knight' },
                ].map((opt) => (
                  <button
                    key={opt.p}
                    className="cm-promo-btn"
                    title={opt.title}
                    disabled={submittingMove}
                    onClick={async () => {
                      try {
                        setError('');
                        setSubmittingMove(true);
                        optimisticSquaresRef.current = { from: pendingPromotion.from, to: pendingPromotion.to };

                        // Compute the correct FEN for the CHOSEN piece from the
                        // authoritative server FEN — NOT a pre-stored queen FEN.
                        try {
                          const preview = new Chess(gameRef.current?.fen() || fen || INITIAL_CHESS_FEN);
                          const promoMove = preview.move({ from: pendingPromotion.from, to: pendingPromotion.to, promotion: opt.p });
                          if (promoMove) {
                            applyOptimisticFen(preview.fen());
                            playMoveSoundNextFrame({ isCheck: preview.isCheck() });
                          } else {
                            playMoveSoundNextFrame();
                          }
                        } catch {
                          playMoveSoundNextFrame();
                        }

                        localSoundPlyRef.current = Math.max(localSoundPlyRef.current, moves.length + 1);
                        await submitMoveToServer({ from: pendingPromotion.from, to: pendingPromotion.to, promotion: opt.p });
                        setPendingPromotion(null);
                      } catch (e) {
                        setError(e.response?.data?.error || 'Failed to submit promotion');
                        optimisticFenRef.current = null;
                        optimisticSquaresRef.current = null;
                        syncGameFromMatch(match);
                      } finally {
                        setSubmittingMove(false);
                      }
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
                <button
                  className="cm-promo-btn cm-promo-btn--cancel"
                  disabled={submittingMove}
                  onClick={() => {
                    optimisticFenRef.current = null;
                    optimisticSquaresRef.current = null;
                    setPendingPromotion(null);
                    syncGameFromMatch(match);
                  }}
                >
                  ✖
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── MY TIMER (bottom) ─── */}
      <div className={`cm-player-bar${bottomTimer.isTurn ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__avatar">{bottomTimer.label === 'Black' ? '♚' : '♔'}</span>
          <div className="cm-player-bar__meta">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="cm-player-bar__name">{bottomTimerName}</span>
              <span className="cm-player-bar__rating">
              ⭐ {bottomTimer.label === 'Black' ? (match?.black_rating || match?.opponent_rating || 1200) : (match?.white_rating || match?.creator_rating || 1200)}
            </span>
              {(bottomTimer.label === 'Black' ? (asNumber(match?.black_streak) || asNumber(match?.opponent_streak)) : (asNumber(match?.white_streak) || asNumber(match?.creator_streak))) >= 3 && (
                <span style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 2 }}>
                  🔥 <span style={{ color: '#f97316', fontWeight: 700 }}>{bottomTimer.label === 'Black' ? (match?.black_streak || match?.opponent_streak) : (match?.white_streak || match?.creator_streak)}</span>
                </span>
              )}
            </div>
            <div className="cm-captured-row">
              {getCapturedDisplay(bottomTimer.label).pieces.map((piece, index) => (
                <span key={`${bottomTimer.label}-cap-${index}`} className="cm-captured-piece">{piece}</span>
              ))}
              {getCapturedDisplay(bottomTimer.label).diff && (
                <span className="cm-captured-diff">{getCapturedDisplay(bottomTimer.label).diff}</span>
              )}
            </div>
          </div>
          {match?.status === 'in_progress' && isMyTurn && (
            <span className="cm-turn-chip">⚡ Your turn{submittingMove ? '…' : ''}</span>
          )}
          {isSpectator && <span className="cm-turn-chip">👁 Spectating</span>}
        </div>
        <div className={`cm-clock${bottomTimer.isTurn ? ' cm-clock--ticking' : ''}`}>{formatClock(bottomTimer.ms)}</div>
      </div>

      </div>
      {/* ─── END GAME STACK ─── */}

      {/* ─── MOVES STRIP (horizontal scroll, below board) ─── */}
      <div className="cm-moves-strip" ref={movesStripRef}>
        {moves.length === 0 && <span className="cm-moves-empty">No moves yet</span>}
        {movePairs.map((pair) => {
          const whitePly = (pair.move * 2) - 1;
          const blackPly = pair.move * 2;
          return (
            <div key={`mp-${pair.move}`} className="cm-moves-pair">
              <span className="cm-moves-num">{pair.move}.</span>
              <button
                type="button"
                className={`cm-moves-san ${effectiveReviewPly === whitePly ? 'cm-moves-san--active' : ''}`}
                onClick={() => setReviewPly(whitePly)}
              >
                {pair.white?.san || (pair.white ? `${pair.white.from_sq}${pair.white.to_sq}` : '…')}
              </button>
              {pair.black && (
                <button
                  type="button"
                  className={`cm-moves-san cm-moves-san--black ${effectiveReviewPly === blackPly ? 'cm-moves-san--active' : ''}`}
                  onClick={() => setReviewPly(blackPly)}
                >
                  {pair.black?.san || `${pair.black.from_sq}${pair.black.to_sq}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── RESULT BANNER (completed only) ─── */}
      {isCompleted && (
        <div className={`cm-result-banner${didIWin ? ' cm-result-banner--win' : didILose ? ' cm-result-banner--loss' : ' cm-result-banner--draw'}`}>
          <span className="cm-result-banner__icon">{didIWin ? '🏆' : didILose ? '💔' : '🤝'}</span>
          <span className="cm-result-banner__text">{winnerText}</span>
          {asNumber(match?.wager) > 0 && (
            <span className="cm-result-banner__payout">
              Pot {asNumber(match?.wager) * 2} • Charge {Number.isFinite(Number(match?.charge_amount)) ? match?.charge_amount : Math.floor(asNumber(match?.wager) * 2 * 0.07)} • Net {Number.isFinite(Number(match?.net_pot)) ? match?.net_pot : Math.max(0, Math.floor(asNumber(match?.wager) * 2 * 0.93))}
            </span>
          )}
        </div>
      )}

      {inactivityCancelled && (
        <div className="cm-result-banner cm-result-banner--draw">
          <span className="cm-result-banner__text">Cancelled — no moves were made</span>
        </div>
      )}

      {/* ──────── STATUS + ACTIONS ──────── */}
      <div className="cm-status-line">
        {match?.status === 'pending' && (
          <>
            {statusText}
            {inviteLink && (
              <button className="cm-link-btn" onClick={copyInviteLink}>📋 Copy invite link</button>
            )}
          </>
        )}
      </div>

      <div className="cm-actions">
        {canAccept && (
          <button
            className="cm-btn cm-btn--primary"
            onClick={async () => {
              try {
                setError('');
                await chessAPI.acceptMatch(matchId);
                refreshUser?.();
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to accept challenge');
              }
            }}
          >
            Accept Challenge
          </button>
        )}

        {canJoinOpenRoom && (
          <button
            className="cm-btn cm-btn--primary"
            onClick={async () => {
              try {
                setError('');
                await chessAPI.joinMatch(matchId);
                refreshUser?.();
                await load();
              } catch (e) {
                const msg = String(e?.response?.data?.error || '').toLowerCase();
                if (msg.includes('full') || msg.includes('joined') || e?.response?.status === 409) {
                  setError('Room is full. You are spectating.');
                } else {
                  setError(e.response?.data?.error || 'Failed to join room');
                }
              }
            }}
          >
            Join Challenge
          </button>
        )}

        {isRoomFullForViewer && (
          <div className="cm-status-line">Room full — watch mode</div>
        )}

        {canCancel && (
          <button
            className="cm-btn cm-btn--ghost"
            onClick={async () => {
              try {
                setError('');
                await chessAPI.cancelMatch(matchId);
                refreshUser?.();
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to cancel');
              }
            }}
          >
            Cancel
          </button>
        )}

        {canAbort && (
          <button
            className="cm-btn cm-btn--danger"
            onClick={async () => {
              if (!window.confirm('Abort this match? No penalty will be applied.')) return;
              try {
                setError('');
                await chessAPI.abortMatch(matchId);
                refreshUser?.();
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to abort');
              }
            }}
          >
            Abort Match
          </button>
        )}

        {canOfferDraw && (
          <button
            className="cm-btn"
            style={{ backgroundColor: '#475569', color: '#fff' }}
            onClick={async () => {
              if (!window.confirm('Offer a Draw to your opponent?')) return;
              try {
                setError('');
                await chessAPI.offerDraw(matchId);
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to offer Draw');
              }
            }}
          >
            Offer Draw
          </button>
        )}

        {isDrawOfferedByMe && (
          <button
            className="cm-btn cm-btn--outline"
            disabled
            style={{ opacity: 0.7 }}
          >
            Draw Offered...
          </button>
        )}

        {canAcceptDraw && (
          <button
            className="cm-btn"
            style={{ backgroundColor: '#10b981', color: '#fff' }}
            onClick={async () => {
              if (!window.confirm('Accept the Draw offer? The game will end in a tie.')) return;
              try {
                setError('');
                await chessAPI.acceptDraw(matchId);
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to accept Draw');
              }
            }}
          >
            Accept Draw
          </button>
        )}

        {canResign && (
          <button
            className="cm-btn cm-btn--danger"
            onClick={async () => {
              if (!window.confirm('Are you sure you want to resign?')) return;
              try {
                setError('');
                await chessAPI.resign(matchId);
                refreshUser?.();
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to resign');
              }
            }}
          >
            Resign
          </button>
        )}

        {canBerserk && (
          <button
            className="cm-btn cm-btn--danger"
            style={{ backgroundColor: '#ef4444', color: '#fff' }}
            onClick={handleBerserk}
          >
            🔥 Go Berserk
          </button>
        )}

        {match?.status === 'in_progress' && (
          <button
            className="cm-btn cm-btn--ghost"
            onClick={async () => {
              try {
                setError('');
                await chessAPI.claimTimeout(matchId);
                refreshUser?.();
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Timeout claim failed');
              }
            }}
          >
            ± Claim Timeout
          </button>
        )}

        {canCreateRematch && !match?.tournament_id && (
          <button
            className="cm-btn cm-btn--primary"
            disabled={rematchBusy}
            onClick={handleRematchCreate}
          >
            {rematchBusy ? 'Wait…' : `🔄 Rematch${RematchWager > 0 ? ` (${RematchWager})` : ''}`}
          </button>
        )}

        {isCompleted && isParticipant && rematchCandidate && !match?.tournament_id && (
          <>
            <button
              className="cm-btn cm-btn--primary"
              disabled={rematchBusy}
              onClick={canAcceptRematch ? handleRematchAccept : () => navigate(`/chess/duel/${rematchCandidate.id}`)}
            >
              {rematchBusy
                ? 'Wait…'
                : canAcceptRematch
                  ? `✅ Accept Rematch${asNumber(rematchCandidate.wager) > 0 ? ` (${asNumber(rematchCandidate.wager)})` : ''}`
                  : '🔄 Open Rematch'}
            </button>
            {canAcceptRematch && (
              <button
                className="cm-btn cm-btn--ghost"
                disabled={rematchBusy}
                onClick={handleRematchDecline}
              >
                Decline
              </button>
            )}
          </>
        )}

        {isCompleted && (
          <button
            className="cm-btn cm-btn--ghost"
            onClick={() => {
              if (activeTournamentId) {
                navigate(`/chess/tournaments/${activeTournamentId}`);
              } else {
                navigate('/chess');
              }
            }}
          >
            {activeTournamentId ? 'Back to Tournament' : 'Back to Lobby'}
          </button>
        )}
      </div>

      <div className="cm-review-dock">
        {!match?.tournament_id && (
          <button
            type="button"
            className="cm-dock-btn"
            title="Open chat"
            onClick={() => setShowChat(true)}
          >
            💬
            {unreadChatCount > 0 && <span className="cm-dock-badge">{unreadChatCount > 9 ? '9+' : unreadChatCount}</span>}
          </button>
        )}
        <button
          type="button"
          className="cm-dock-btn"
          title="Previous move"
          onClick={() => setReviewPly((prev) => {
            const current = prev == null ? livePly : prev;
            return Math.max(0, current - 1);
          })}
          disabled={!canStepBackward}
        >
          ‹
        </button>
        <button
          type="button"
          className={`cm-dock-btn cm-dock-btn--live ${!isReviewing ? 'cm-dock-btn--active' : ''}`}
          title="Return to live board"
          onClick={() => setReviewPly(null)}
        >
          {isReviewing ? reviewMoveLabel : 'LIVE'}
        </button>
        <button
          type="button"
          className="cm-dock-btn"
          title="Next move"
          onClick={() => setReviewPly((prev) => {
            const current = prev == null ? livePly : prev;
            const next = Math.min(livePly, current + 1);
            return next >= livePly ? null : next;
          })}
          disabled={!canStepForward}
        >
          ›
        </button>
      </div>

      <QuickChat
        socket={chessDuelSocket.socket || (typeof getSocket === 'function' ? getSocket() : null)}
        roomId={matchId}
        meId={user?.id}
        gameType="chess"
      />
    </div>
  );
}

